export const formatNumber = (number, options = {}) => {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    currency,
  } = options;

  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits,
    maximumFractionDigits,
    ...(currency && { style: "currency", currency }),
  });

  return formatter.format(number);
};

export const formatDate = (timestamp, format) => {
  if (!timestamp) return "N/A";

  // If input is a string in YYYY-MM-DD or YYYY/MM/DD format, return as is
  if (typeof timestamp === 'string' && /^\d{4}[-/]\d{2}[-/]\d{2}$/.test(timestamp)) {
    return timestamp;
  }

  // Handle Unix timestamp (seconds since epoch)
  const date = new Date(
    typeof timestamp === "number" ? timestamp : timestamp * 1000
  );
  if (isNaN(date.getTime())) return "N/A";

  if (format) {
    // Custom format requested
    const parts = {
      DD: date.getDate().toString().padStart(2, "0"),
      MM: (date.getMonth() + 1).toString().padStart(2, "0"),
      YYYY: date.getFullYear(),
      HH: date.getHours().toString().padStart(2, "0"),
      mm: date.getMinutes().toString().padStart(2, "0"),
      ss: date.getSeconds().toString().padStart(2, "0"),
    };

    return format.replace(/DD|MM|YYYY|HH|mm|ss/g, (match) => parts[match]);
  }

  const today = new Date();

  // If it's today, show time only
  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  // For all other dates, show MM/DD/YY HH:mm
  return date
    .toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(",", "");
};

/**
 * Format a phone number with proper spacing and formatting
 * @param {string} phoneNumber - The phone number to format (e.g., +2341234567890)
 * @returns {string} - The formatted phone number (e.g., +234 123 456 7890)
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "N/A";

  // Remove any non-digit characters except the plus sign
  const cleaned = phoneNumber.replace(/[^\d+]/g, "");

  // Check if it starts with a plus sign
  const hasPlus = cleaned.startsWith("+");

  // Handle different formats based on length and country code
  if (hasPlus) {
    // International format
    const countryCode = cleaned.substring(1, 4); // Extract potential country code (1-3 digits)
    const restOfNumber = cleaned.substring(4);

    // Format based on length of the rest of the number
    if (restOfNumber.length <= 4) {
      return `+${countryCode} ${restOfNumber}`;
    } else if (restOfNumber.length <= 7) {
      return `+${countryCode} ${restOfNumber.substring(
        0,
        3
      )} ${restOfNumber.substring(3)}`;
    } else {
      // Split into groups of 3-3-4 or similar pattern
      const part1 = restOfNumber.substring(0, 3);
      const part2 = restOfNumber.substring(3, 6);
      const part3 = restOfNumber.substring(6);

      return `+${countryCode} ${part1} ${part2} ${part3}`;
    }
  } else {
    // Just add spaces every 3-4 digits if no plus sign
    if (cleaned.length <= 4) {
      return cleaned;
    } else if (cleaned.length <= 7) {
      return `${cleaned.substring(0, 3)} ${cleaned.substring(3)}`;
    } else if (cleaned.length <= 10) {
      return `${cleaned.substring(0, 3)} ${cleaned.substring(
        3,
        6
      )} ${cleaned.substring(6)}`;
    } else {
      // For longer numbers, try to format intelligently
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})$/, "$1 $2 $3");
    }
  }
};
